/**
 * Deterministic rule-content conversion between tool-config providers.
 *
 * Each provider stores the same conceptual content with a different
 * on-disk shape:
 *   - claude:   CLAUDE.md (plain markdown, no wrapping)
 *   - opencode: AGENTS.md (plain markdown, no wrapping)
 *   - copilot:  .github/copilot-instructions.md (plain markdown, no wrapping)
 *   - cursor:   .cursorrules or .cursor/rules/*.mdc (content wrapped in ``` fences)
 *
 * Plain ↔ plain conversions are identity (the same body travels unchanged).
 * Cursor wraps content in a single fenced code block on disk; unwrap on read,
 * re-wrap on write.  No AI calls — every transform is a pure string rewrite.
 */

export type Provider = "claude" | "cursor" | "copilot" | "opencode";

export const PROVIDERS: readonly Provider[] = [
  "claude",
  "cursor",
  "copilot",
  "opencode",
] as const;

export function isProvider(value: string): value is Provider {
  return (PROVIDERS as readonly string[]).includes(value);
}

export class UnknownProviderError extends Error {
  constructor(value: string) {
    super(
      `Unknown provider "${value}". Use one of: ${PROVIDERS.join(", ")}`
    );
    this.name = "UnknownProviderError";
  }
}

const CURSOR_OPEN = "```\n";
const CURSOR_CLOSE = "\n```";

/**
 * Strip the outer fenced code block Cursor wraps content in.  If no fence is
 * present, returns the content unchanged.
 */
export function unwrapCursor(content: string): string {
  const trimmedStart = content.replace(/^\s+/, "");
  if (!trimmedStart.startsWith("```")) return content;
  const firstNewline = trimmedStart.indexOf("\n");
  if (firstNewline === -1) return content;
  const closingIdx = trimmedStart.lastIndexOf("\n```");
  if (closingIdx <= firstNewline) return content;
  return trimmedStart.slice(firstNewline + 1, closingIdx);
}

/**
 * Wrap content in Cursor's outer fenced code block.
 * Idempotent: if already wrapped, returns content unchanged.
 */
export function wrapCursor(content: string): string {
  if (isCursorWrapped(content)) return content;
  return `${CURSOR_OPEN}${content}${CURSOR_CLOSE}`;
}

function isCursorWrapped(content: string): boolean {
  const t = content.trimStart();
  return t.startsWith("```") && t.endsWith("```");
}

/**
 * Convert rule content from one provider's on-disk shape to another's.
 * Pure and deterministic — no I/O, no AI calls.
 *
 * Cursor ↔ anything goes through wrap/unwrap; the other three providers
 * share plain markdown so any conversion between them is identity.
 */
export function convertRuleContent(
  content: string,
  from: Provider,
  to: Provider
): string {
  if (!isProvider(from)) throw new UnknownProviderError(from);
  if (!isProvider(to)) throw new UnknownProviderError(to);

  if (from === to) return content;

  if (from === "cursor" && to !== "cursor") {
    return unwrapCursor(content);
  }

  if (to === "cursor" && from !== "cursor") {
    return wrapCursor(content);
  }

  return content;
}

/**
 * Default file path hint for a provider.  Useful for the CLI to suggest where
 * the output would land in a real project.  Does not write — the CLI only
 * writes when `--out` is given.
 */
export function defaultPathFor(provider: Provider): string {
  switch (provider) {
    case "claude":
      return "CLAUDE.md";
    case "cursor":
      return ".cursor/rules/cai.mdc";
    case "copilot":
      return ".github/copilot-instructions.md";
    case "opencode":
      return "AGENTS.md";
  }
}